import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface HistoryEntry {
  pathname: string;
  timestamp: number;
}

const RETURN_PATH_TTL_MS = 1000 * 60 * 15;

const isValidEntry = (entry: HistoryEntry | null | undefined): entry is HistoryEntry =>
  Boolean(
    entry?.pathname?.startsWith("/") &&
      Date.now() - entry.timestamp <= RETURN_PATH_TTL_MS &&
      entry.pathname !== "/" &&
      !entry.pathname.startsWith("/auth/"),
  );

interface NavigationHistoryState {
  navigationHistory: HistoryEntry[];
  addLastUnauthorizedEntry: (entry: HistoryEntry) => HistoryEntry | null;
  mergeNavigationHistory: () => void;
  clearHistory: () => void;
  getLastEntry: () => HistoryEntry | null;
}

export const useNavigationHistoryStore = create<NavigationHistoryState>()(
  persist(
    (set, get) => ({
      navigationHistory: [],

      addLastUnauthorizedEntry: (entry) => {
        set({ navigationHistory: [entry] });

        return entry;
      },

      clearHistory: () => set({ navigationHistory: [] }),

      mergeNavigationHistory: () => {
        const sessionStorageHistory = sessionStorage.getItem("navigation-history");

        if (!sessionStorageHistory) {
          set({ navigationHistory: [] });
          return;
        }

        try {
          const parsedHistory = JSON.parse(sessionStorageHistory).state?.navigationHistory?.[0] as
            | HistoryEntry
            | undefined;
          set({ navigationHistory: isValidEntry(parsedHistory) ? [parsedHistory] : [] });
        } catch {
          set({ navigationHistory: [] });
        }
      },

      getLastEntry: () => {
        const { navigationHistory } = get();
        const entry = navigationHistory[0];
        return isValidEntry(entry) ? entry : null;
      },
    }),
    {
      name: "navigation-history",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
